import type { Messages } from "@lingui/core";

import { loadWidgetI18nCatalogs } from "./i18n/load-catalogs";
import type { SupportedLocale } from "./i18n/locale";
import { initializeIframeTelemetry } from "./iframe-telemetry";
import { initializeSentry } from "./sentry-config";

declare global {
  interface Window {
    /** Semantic version of the running widget build (package.json), set at bootstrap. */
    version?: string;
  }
}

export type WidgetBootstrapOptions = {
  packageVersion: string;
  appDisplayName: string;
  i18nCatalogs: Record<SupportedLocale, Messages>;
  /** Optional async work before the app mounts (e.g. config preload). */
  preload?: () => Promise<void>;
};

/**
 * Shared browser bootstrap: Sentry, iframe telemetry, i18n catalogues, optional preload.
 * Call from each product/showcase `main.tsx` before creating the router and mounting React.
 */
export async function runWidgetBootstrap(options: WidgetBootstrapOptions): Promise<void> {
  const { packageVersion, appDisplayName, i18nCatalogs, preload } = options;

  if (typeof window !== "undefined") {
    window.version = packageVersion;
  }

  initializeSentry({
    release: packageVersion,
    appDisplayName,
  });
  initializeIframeTelemetry();
  loadWidgetI18nCatalogs(i18nCatalogs);

  if (preload) {
    await preload();
  }
}
